/**
 * ThemeThumbnail — 9:16 mini phone-screen preview card for a theme.
 *
 * Purely static React Native views (no animations, no WebView).
 * Each theme has a handcrafted config that captures its visual identity.
 *
 * To add a new theme thumbnail:
 *  1. Add a ThumbnailConfig entry to THUMBNAIL_CONFIGS below (keyed by theme id).
 *  2. The ThemeThumbnail component picks it up automatically.
 */

import {type ReactNode} from 'react'
import {StyleSheet, Text, View} from 'react-native'
import {LinearGradient} from 'expo-linear-gradient'

// ── Config types ──────────────────────────────────────────────────────────────

export interface ThumbnailConfig {
  /** Background: solid colour or two-stop gradient */
  bg: string
  bgGradient?: readonly [string, string, ...string[]]

  /** Top decorative strip (15% height) */
  topType?: 'stripes' | 'blocks' | 'rain' | 'dots' | 'crown' | 'none'
  topColor?: string
  topAccent?: string

  /** Main card */
  cardBg: string
  cardBorder?: string
  cardRadius?: number

  /** Question bars (simulated text) */
  qBar1Color: string
  qBar2Color: string
  qBar2Width?: number // 0–1, default 0.7

  /** Option bars */
  opt1Bg: string
  opt1Border?: string
  opt2Bg: string
  opt2Border?: string

  /** Bottom progress bar */
  progressColor: string
  progressFill?: number // 0–1, default 0.35

  /** Floating accent orbs (e.g. liquid glass) */
  orbs?: {
    color: string
    size: number
    top: number
    left: number
    opacity: number
  }[]

  /** Centre overlay symbol (emoji/text) — used for custom upload */
  centerSymbol?: string
  centerSymbolColor?: string

  /** HP bar (pixel dungeon) */
  hpBar?: boolean
  hpColor?: string
  hpFill?: number // 0–1
}

// ── Per-theme configs ─────────────────────────────────────────────────────────

export const THUMBNAIL_CONFIGS: Record<string, ThumbnailConfig> = {
  // ── Classic Quiz ────────────────────────────────────────────────────────────
  'classic-quiz': {
    bg: '#0a1220',
    cardBg: '#131f2e',
    cardBorder: '#1e3048',
    qBar1Color: '#e8f1f8',
    qBar2Color: '#6b8299',
    opt1Bg: '#0085ff',
    opt2Bg: '#1a2d42',
    opt2Border: '#1e3048',
    progressColor: '#0085ff',
    progressFill: 0.35,
  },

  // ── Summer ──────────────────────────────────────────────────────────────────
  summer: {
    bg: '#ff9a3c',
    bgGradient: ['#ffd166', '#ff9a3c', '#f77f00'],
    topType: 'dots',
    topColor: '#ffeaa7',
    cardBg: '#fffdf0',
    cardBorder: '#ffe8a3',
    cardRadius: 12,
    qBar1Color: '#1a1a2e',
    qBar2Color: '#6b5b3e',
    opt1Bg: '#ff9a3c',
    opt2Bg: '#ffe8a3',
    opt2Border: '#f59e0b',
    progressColor: '#f59e0b',
    progressFill: 0.5,
  },

  // ── Liquid Glass ────────────────────────────────────────────────────────────
  'liquid-glass': {
    bg: '#120027',
    bgGradient: ['#120027', '#2d1065', '#0f0024'],
    cardBg: 'rgba(255,255,255,0.08)',
    cardBorder: 'rgba(255,255,255,0.18)',
    qBar1Color: 'rgba(255,255,255,0.85)',
    qBar2Color: 'rgba(255,255,255,0.45)',
    opt1Bg: 'rgba(168,85,247,0.35)',
    opt1Border: 'rgba(168,85,247,0.6)',
    opt2Bg: 'rgba(255,255,255,0.1)',
    opt2Border: 'rgba(255,255,255,0.2)',
    progressColor: '#a855f7',
    progressFill: 0.4,
    orbs: [
      {color: '#a855f7', size: 28, top: 5, left: 2, opacity: 0.35},
      {color: '#6d28d9', size: 20, top: 25, left: 55, opacity: 0.3},
      {color: '#ec4899', size: 16, top: 55, left: 10, opacity: 0.25},
    ],
  },

  // ── Sprout Quest (pixel-art) ────────────────────────────────────────────────
  'pixel-art': {
    bg: '#2a1a0e',
    topType: 'dots',
    topColor: '#22c55e',
    cardBg: '#c8a97e',
    cardBorder: '#8b5e3c',
    cardRadius: 4,
    qBar1Color: '#3d1f00',
    qBar2Color: '#5a3a1a',
    opt1Bg: '#5a8a2f',
    opt1Border: '#3a5a1f',
    opt2Bg: '#c8a97e',
    opt2Border: '#8b5e3c',
    progressColor: '#22c55e',
    progressFill: 0.3,
  },

  // ── HTML Bridge (custom upload) ─────────────────────────────────────────────
  'html-bridge': {
    bg: '#0d1f1e',
    cardBg: 'transparent',
    cardBorder: '#14b8a6',
    qBar1Color: '#14b8a6',
    qBar2Color: '#0d9488',
    opt1Bg: 'rgba(20,184,166,0.15)',
    opt1Border: '#14b8a6',
    opt2Bg: 'rgba(20,184,166,0.08)',
    opt2Border: '#0d9488',
    progressColor: '#14b8a6',
    progressFill: 0.0,
    centerSymbol: '</>',
    centerSymbolColor: '#14b8a6',
  },

  // ── Clash Royale ────────────────────────────────────────────────────────────
  'clash-royale': {
    bg: '#4dba28',
    bgGradient: ['#5ccf30', '#3a9420'],
    topType: 'crown',
    topColor: '#c89b3c',
    cardBg: '#1a4a7a',
    cardBorder: '#c89b3c',
    qBar1Color: '#ffffff',
    qBar2Color: '#a0c4e0',
    opt1Bg: '#c89b3c',
    opt1Border: '#f0c060',
    opt2Bg: '#1a3a5a',
    opt2Border: '#c89b3c',
    progressColor: '#c89b3c',
    progressFill: 0.45,
  },

  // ── League of Legends ───────────────────────────────────────────────────────
  'league-of-legends': {
    bg: '#0a0e1f',
    bgGradient: ['#0a0e1f', '#0d1530', '#080c1a'],
    topType: 'dots',
    topColor: '#c89b3c',
    cardBg: 'rgba(200,155,60,0.08)',
    cardBorder: '#c89b3c',
    qBar1Color: '#e8d5a0',
    qBar2Color: '#c89b3c',
    opt1Bg: 'rgba(200,155,60,0.2)',
    opt1Border: '#c89b3c',
    opt2Bg: 'rgba(200,155,60,0.08)',
    opt2Border: 'rgba(200,155,60,0.4)',
    progressColor: '#c89b3c',
    progressFill: 0.55,
  },

  // ── Minecraft ───────────────────────────────────────────────────────────────
  minecraft: {
    bg: '#5a3a1a',
    topType: 'blocks',
    topColor: '#5a8a2f',
    topAccent: '#4a7a1f',
    cardBg: '#8b5e3c',
    cardBorder: '#3d2a0e',
    cardRadius: 2,
    qBar1Color: '#ffd700',
    qBar2Color: '#c8a050',
    opt1Bg: '#5a8a2f',
    opt1Border: '#3a5a1f',
    opt2Bg: '#6b4a2a',
    opt2Border: '#3d2a0e',
    progressColor: '#5a8a2f',
    progressFill: 0.6,
  },

  // ── Matrix ──────────────────────────────────────────────────────────────────
  matrix: {
    bg: '#000000',
    topType: 'rain',
    topColor: '#00ff41',
    cardBg: '#050f05',
    cardBorder: '#00ff41',
    cardRadius: 0,
    qBar1Color: '#00ff41',
    qBar2Color: '#009920',
    opt1Bg: 'rgba(0,255,65,0.12)',
    opt1Border: '#00ff41',
    opt2Bg: 'rgba(0,255,65,0.06)',
    opt2Border: '#009920',
    progressColor: '#00ff41',
    progressFill: 0.4,
  },

  // ── Pixel Dungeon ───────────────────────────────────────────────────────────
  'pixel-dungeon': {
    bg: '#0a0012',
    bgGradient: ['#0a0012', '#150025', '#0a0012'],
    cardBg: '#1a0030',
    cardBorder: '#00ffff',
    cardRadius: 4,
    qBar1Color: '#00ffff',
    qBar2Color: '#a855f7',
    opt1Bg: 'rgba(0,255,255,0.15)',
    opt1Border: '#00ffff',
    opt2Bg: 'rgba(255,0,255,0.1)',
    opt2Border: '#ff00ff',
    progressColor: '#00ffff',
    progressFill: 0.3,
    hpBar: true,
    hpColor: '#ef4444',
    hpFill: 0.65,
  },

  // ── Batman ──────────────────────────────────────────────────────────────────
  batman: {
    bg: '#0a0a0a',
    bgGradient: ['#1a1a1a', '#0a0a0a'],
    topType: 'dots',
    topColor: '#ffd700',
    cardBg: '#1a1a1a',
    cardBorder: '#ffd700',
    qBar1Color: '#ffd700',
    qBar2Color: '#b8860b',
    opt1Bg: '#ffd700',
    opt1Border: '#ffd700',
    opt2Bg: 'rgba(255,215,0,0.1)',
    opt2Border: '#ffd700',
    progressColor: '#ffd700',
    progressFill: 0.5,
  },

  // ── La Casa de Papel ────────────────────────────────────────────────────────
  lacasa: {
    bg: '#8b0000',
    bgGradient: ['#cc0000', '#8b0000', '#5c0000'],
    topType: 'stripes',
    topColor: '#cc0000',
    topAccent: '#ff2222',
    cardBg: '#1a0000',
    cardBorder: '#cc0000',
    qBar1Color: '#ffffff',
    qBar2Color: '#ffcccc',
    opt1Bg: '#cc0000',
    opt1Border: '#ff3333',
    opt2Bg: 'rgba(204,0,0,0.2)',
    opt2Border: '#cc0000',
    progressColor: '#cc0000',
    progressFill: 0.45,
  },

  // ── Squid Game ──────────────────────────────────────────────────────────────
  squidgame: {
    bg: '#1a0030',
    bgGradient: ['#2d0050', '#1a0030', '#0d001a'],
    topType: 'dots',
    topColor: '#ff2d55',
    cardBg: '#2d0050',
    cardBorder: '#ff2d55',
    qBar1Color: '#ffffff',
    qBar2Color: '#ff9ec0',
    opt1Bg: '#ff2d55',
    opt1Border: '#ff6688',
    opt2Bg: 'rgba(255,45,85,0.15)',
    opt2Border: '#ff2d55',
    progressColor: '#ff2d55',
    progressFill: 0.4,
  },

  // ── Neubrutalism ────────────────────────────────────────────────────────────
  neubrutalism: {
    bg: '#fff176',
    topType: 'stripes',
    topColor: '#000000',
    topAccent: '#fff176',
    cardBg: '#ffffff',
    cardBorder: '#000000',
    cardRadius: 0,
    qBar1Color: '#000000',
    qBar2Color: '#333333',
    opt1Bg: '#000000',
    opt1Border: '#000000',
    opt2Bg: '#ffffff',
    opt2Border: '#000000',
    progressColor: '#000000',
    progressFill: 0.5,
  },
}

/** Fallback config for unknown theme ids */
const FALLBACK_CONFIG: ThumbnailConfig = {
  bg: '#0a1220',
  cardBg: '#131f2e',
  cardBorder: '#1e3048',
  qBar1Color: '#e8f1f8',
  qBar2Color: '#6b8299',
  opt1Bg: '#0085ff',
  opt2Bg: '#1a2d42',
  progressColor: '#0085ff',
}

// ── Decorative sub-components ─────────────────────────────────────────────────

function TopStripes({color, accent}: {color: string; accent?: string}) {
  return (
    <View style={tw.topStripes}>
      {Array.from({length: 6}).map((_, i) => (
        <View
          key={i}
          style={[
            tw.stripe,
            {backgroundColor: i % 2 === 0 ? color : (accent ?? color + '99')},
          ]}
        />
      ))}
    </View>
  )
}

function TopBlocks({color, accent}: {color: string; accent?: string}) {
  const blocks = [
    {c: accent ?? '#3d2a0e', x: 0},
    {c: color, x: 1},
    {c: accent ?? '#3d2a0e', x: 2},
    {c: color, x: 3},
    {c: accent ?? '#3d2a0e', x: 4},
    {c: color, x: 5},
    {c: accent ?? '#3d2a0e', x: 6},
    {c: color, x: 7},
  ]
  return (
    <View style={tw.topBlocks}>
      {blocks.map((b, i) => (
        <View key={i} style={[tw.block, {backgroundColor: b.c}]} />
      ))}
    </View>
  )
}

function TopRain({color}: {color: string}) {
  const cols = [0.08, 0.22, 0.38, 0.52, 0.68, 0.82]
  return (
    <View style={tw.topRain}>
      {cols.map((left, i) => (
        <View
          key={i}
          style={[
            tw.rainCol,
            {
              left: `${left * 100}%`,
              height: 6 + (i % 3) * 4,
              backgroundColor: color,
              opacity: 0.5 + (i % 3) * 0.2,
            },
          ]}
        />
      ))}
    </View>
  )
}

function TopDots({color}: {color: string}) {
  return (
    <View style={tw.topDots}>
      {Array.from({length: 5}).map((_, i) => (
        <View
          key={i}
          style={[tw.dot, {backgroundColor: color, opacity: 0.4 + i * 0.12}]}
        />
      ))}
    </View>
  )
}

function TopCrown({color}: {color: string}) {
  return (
    <View
      style={[tw.topBase, {alignItems: 'center', justifyContent: 'center'}]}>
      <Text style={{fontSize: 14, color}}> 👑 </Text>
    </View>
  )
}

// ── Main component ────────────────────────────────────────────────────────────

interface ThemeThumbnailProps {
  themeId: string
  /** Override width; height is always set via aspectRatio 9/16 */
  width?: number
}

export function ThemeThumbnail({themeId, width}: ThemeThumbnailProps) {
  const cfg = THUMBNAIL_CONFIGS[themeId] ?? FALLBACK_CONFIG

  const container = [tw.root, width != null && {width}]

  const bg = cfg.bgGradient ? (
    <LinearGradient colors={cfg.bgGradient} style={StyleSheet.absoluteFill} />
  ) : (
    <View style={[StyleSheet.absoluteFill, {backgroundColor: cfg.bg}]} />
  )

  // Orbs (liquid-glass style)
  const orbs = cfg.orbs?.map((o, i) => (
    <View
      key={i}
      style={[
        tw.orb,
        {
          width: o.size,
          height: o.size,
          borderRadius: o.size / 2,
          backgroundColor: o.color,
          opacity: o.opacity,
          top: `${o.top}%`,
          left: `${o.left}%`,
        },
      ]}
    />
  ))

  // Top decoration
  let topDecor: ReactNode = null
  switch (cfg.topType) {
    case 'stripes':
      topDecor = (
        <TopStripes color={cfg.topColor ?? '#ffffff'} accent={cfg.topAccent} />
      )
      break
    case 'blocks':
      topDecor = (
        <TopBlocks color={cfg.topColor ?? '#5a8a2f'} accent={cfg.topAccent} />
      )
      break
    case 'rain':
      topDecor = <TopRain color={cfg.topColor ?? '#00ff41'} />
      break
    case 'dots':
      topDecor = <TopDots color={cfg.topColor ?? '#ffffff'} />
      break
    case 'crown':
      topDecor = <TopCrown color={cfg.topColor ?? '#c89b3c'} />
      break
    default:
      topDecor = <View style={tw.topBase} />
  }

  const qBar2W = cfg.qBar2Width ?? 0.72
  const progFill = cfg.progressFill ?? 0.35

  return (
    <View style={container}>
      {/* Background */}
      {bg}

      {/* Floating orbs */}
      {orbs}

      {/* HP bar — pixel dungeon / game themes */}
      {cfg.hpBar && (
        <View style={tw.hpContainer}>
          <View style={tw.hpTrack}>
            <View
              style={[
                tw.hpFill,
                {
                  width: `${(cfg.hpFill ?? 0.65) * 100}%`,
                  backgroundColor: cfg.hpColor ?? '#ef4444',
                },
              ]}
            />
          </View>
          <View style={tw.hpTrack}>
            <View
              style={[tw.hpFill, {width: '40%', backgroundColor: '#a855f7'}]}
            />
          </View>
        </View>
      )}

      {/* Top decoration strip */}
      {topDecor}

      {/* Centre card */}
      <View style={tw.cardArea}>
        <View
          style={[
            tw.card,
            {
              backgroundColor: cfg.cardBg,
              borderColor: cfg.cardBorder ?? 'transparent',
              borderRadius: cfg.cardRadius ?? 8,
            },
          ]}>
          {/* Center symbol (upload / code) */}
          {cfg.centerSymbol ? (
            <View style={tw.centerSymbolWrap}>
              <Text
                style={[
                  tw.centerSymbol,
                  {color: cfg.centerSymbolColor ?? '#14b8a6'},
                ]}>
                {cfg.centerSymbol}
              </Text>
            </View>
          ) : (
            <>
              {/* Question bars */}
              <View style={[tw.qBar, {backgroundColor: cfg.qBar1Color}]} />
              <View
                style={[
                  tw.qBar,
                  {
                    backgroundColor: cfg.qBar2Color,
                    width: `${qBar2W * 100}%`,
                    marginTop: 4,
                  },
                ]}
              />

              {/* Spacer */}
              <View style={{height: 10}} />

              {/* Option 1 */}
              <View
                style={[
                  tw.optBar,
                  {
                    backgroundColor: cfg.opt1Bg,
                    borderColor: cfg.opt1Border ?? 'transparent',
                  },
                ]}
              />

              {/* Option 2 */}
              <View
                style={[
                  tw.optBar,
                  {
                    backgroundColor: cfg.opt2Bg,
                    borderColor: cfg.opt2Border ?? 'transparent',
                    marginTop: 5,
                  },
                ]}
              />
            </>
          )}
        </View>
      </View>

      {/* Bottom progress bar */}
      <View style={tw.progressTrack}>
        <View
          style={[
            tw.progressFill,
            {width: `${progFill * 100}%`, backgroundColor: cfg.progressColor},
          ]}
        />
      </View>

      {/* Bottom pagination dots */}
      <View style={tw.pagination}>
        {[0, 1, 2].map(i => (
          <View
            key={i}
            style={[
              tw.pageDot,
              {
                backgroundColor:
                  i === 0 ? cfg.progressColor : cfg.progressColor + '44',
                width: i === 0 ? 10 : 4,
              },
            ]}
          />
        ))}
      </View>
    </View>
  )
}

// ── Styles ────────────────────────────────────────────────────────────────────

const tw = StyleSheet.create({
  root: {
    aspectRatio: 9 / 16,
    borderRadius: 12,
    overflow: 'hidden',
    backgroundColor: '#0a1220',
  },

  // Top decoration strip
  topBase: {height: '18%', width: '100%'},
  topStripes: {flexDirection: 'row', height: '18%', width: '100%'},
  stripe: {flex: 1},
  topBlocks: {
    flexDirection: 'row',
    height: '18%',
    width: '100%',
    flexWrap: 'wrap',
  },
  block: {width: '12.5%', height: '100%'},
  topRain: {height: '18%', width: '100%', position: 'relative'},
  rainCol: {position: 'absolute', width: 2, bottom: 0},
  topDots: {
    height: '18%',
    width: '100%',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 6,
  },
  dot: {width: 4, height: 4, borderRadius: 2},

  // Card centre area
  cardArea: {
    flex: 1,
    paddingHorizontal: 10,
    justifyContent: 'center',
    marginTop: -4,
  },
  card: {
    borderWidth: 1,
    padding: 10,
    justifyContent: 'center',
  },

  // Centre symbol (upload)
  centerSymbolWrap: {
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 8,
  },
  centerSymbol: {fontSize: 18, fontWeight: '900'},

  // Question bars
  qBar: {height: 6, borderRadius: 3, width: '100%'},

  // Option bars
  optBar: {
    height: 18,
    borderRadius: 6,
    width: '100%',
    borderWidth: 1,
  },

  // Progress
  progressTrack: {
    height: 3,
    width: '100%',
    backgroundColor: 'rgba(255,255,255,0.08)',
  },
  progressFill: {height: '100%'},

  // Pagination dots
  pagination: {
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'center',
    gap: 3,
    paddingVertical: 5,
  },
  pageDot: {height: 4, borderRadius: 2},

  // Orbs
  orb: {position: 'absolute'},

  // HP bar
  hpContainer: {
    position: 'absolute',
    top: '20%',
    left: 8,
    right: 8,
    gap: 2,
    zIndex: 2,
  },
  hpTrack: {
    height: 4,
    borderRadius: 2,
    backgroundColor: 'rgba(255,255,255,0.15)',
    overflow: 'hidden',
  },
  hpFill: {height: '100%', borderRadius: 2},
})
